import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { AdminLoadError } from '../components/AdminLoadError';
import { UserIdentity } from '../components/UserIdentity';
import { StatusFilterCheckboxes } from '../components/StatusFilterCheckboxes';
import { ArcheChip } from '../components/ArcheChip';
import { ArcheFilterSelect } from '../components/ArcheFilterSelect';
import { SupportRefId, AwaitingReplyFlag, ReplyWaivedCheckbox } from '../components';
import { useReplyWaiver } from '../hooks/useReplyWaiver';
import { useStatusFilterParam } from '../hooks/useStatusFilterParam';
import { useSessionPersistedSearchParams } from '../hooks/useSessionPersistedSearchParams';
import { useSupportUnreadCounts } from '../hooks/useSupportUnreadCounts';
import { useArcheDisplayMap } from '../hooks/useArcheDisplayMap';
import { useArcheFilterParam } from '../hooks/useArcheFilterParam';
import type { SupportUserRef } from '@/lib/api';

interface BugReport {
  bugReportId: string;
  userId: string;
  user?: SupportUserRef | null;
  unread?: boolean;
  awaitingReply?: boolean;
  replyWaived?: boolean;
  archeId?: string | null;
  title: string;
  description: string;
  stepsToReproduce: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  {
    value: 'investigating',
    label: 'Investigating',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

// Remember the bug-reports filter view (status + arche) for the session, so it
// survives opening a report and returning via the bare-route back link.
const VIEW_STORAGE_KEY = 'support-admin-bug-reports-view';
const VIEW_PARAM_KEYS = ['status', 'arche'] as const;

export function AdminBugReportsPage() {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { statuses, setStatuses } = useStatusFilterParam();
  const { arche, setArche } = useArcheFilterParam();
  // Side-effect only: persist/restore the view above (written via those hooks).
  useSessionPersistedSearchParams(VIEW_STORAGE_KEY, VIEW_PARAM_KEYS);
  const { refresh: refreshUnread } = useSupportUnreadCounts();
  const { metaById: archeMeta } = useArcheDisplayMap(bugReports.map((b) => b.archeId));

  // Edit dialog
  const [editReport, setEditReport] = useState<BugReport | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const replyWaiver = useReplyWaiver('bug-reports');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteReport, setDeleteReport] = useState<BugReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBugReports = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await api.listBugReportsAdmin({
        status: statuses.length ? statuses : undefined,
        archeId: arche || undefined,
        limit: 50,
      });
      setBugReports(result.bugReports);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load bug reports:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [statuses, arche]);

  useEffect(() => {
    fetchBugReports();
  }, [fetchBugReports]);

  const handleEdit = (report: BugReport) => {
    setEditReport(report);
    setEditStatus(report.status);
    setEditNotes(report.adminNotes || '');
    replyWaiver.reset(report.replyWaived);
    if (report.unread) {
      setBugReports((prev) =>
        prev.map((b) => (b.bugReportId === report.bugReportId ? { ...b, unread: false } : b)),
      );
      api
        .markBugReportReadAdmin(report.bugReportId)
        .then(() => refreshUnread())
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!editReport) return;
    setSaving(true);
    try {
      // Waiver first: if it fails, nothing has been committed yet.
      await replyWaiver.save(editReport.bugReportId);
      await api.updateBugReportAdmin(editReport.bugReportId, {
        status: editStatus,
        adminNotes: editNotes.trim() || null,
      });
      setEditReport(null);
      await fetchBugReports();
    } catch (err) {
      console.error('Failed to update bug report:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReport) return;
    setDeleting(true);
    try {
      await api.deleteBugReportAdmin(deleteReport.bugReportId);
      setDeleteReport(null);
      await fetchBugReports();
    } catch (err) {
      console.error('Failed to delete bug report:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Bug Reports</h2>
          <p className="text-sm text-muted-foreground">
            {total} report{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <StatusFilterCheckboxes
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={setStatuses}
        />
        <ArcheFilterSelect value={arche} onChange={setArche} />
      </div>

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <AdminLoadError what="bug reports" onRetry={fetchBugReports} />
      ) : bugReports.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No bug reports found.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36px]">
                  <span className="sr-only">Awaiting reply</span>
                </TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="w-[160px]">Bug Report ID</TableHead>
                <TableHead className="w-[160px]">Customer</TableHead>
                <TableHead className="w-[150px]">Arche</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bugReports.map((b) => (
                <TableRow
                  key={b.bugReportId}
                  className="cursor-pointer"
                  onClick={() => handleEdit(b)}
                >
                  <TableCell>
                    <AwaitingReplyFlag awaiting={b.awaitingReply} />
                  </TableCell>
                  <TableCell>
                    <p className="flex items-center gap-1.5 text-sm truncate max-w-[400px]">
                      {b.unread && (
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                          title="Unread"
                        />
                      )}
                      <span className={b.unread ? 'font-semibold' : 'font-medium'}>{b.title}</span>
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 max-w-[400px]">
                      {b.description}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <SupportRefId id={b.bugReportId} label="Bug Report ID" />
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <UserIdentity user={b.user} userId={b.userId} />
                  </TableCell>
                  <TableCell>
                    <ArcheChip
                      archeId={b.archeId}
                      meta={b.archeId ? archeMeta.get(b.archeId) : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={b.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(b.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteReport(b);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editReport} onOpenChange={(open) => !open && setEditReport(null)}>
        <DialogContent>
          {editReport && (
            <>
              <DialogHeader>
                <DialogTitle>{editReport.title}</DialogTitle>
                <SupportRefId
                  id={editReport.bugReportId}
                  label="Bug Report ID"
                  variant="full"
                  className="-ml-1 self-start"
                />
              </DialogHeader>
              <div className="rounded-md bg-muted p-3 max-h-[200px] overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Description:</p>
                <p className="text-sm whitespace-pre-wrap">{editReport.description}</p>
              </div>
              {editReport.stepsToReproduce && (
                <div className="rounded-md bg-muted p-3 max-h-[150px] overflow-y-auto">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Steps to reproduce:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editReport.stepsToReproduce}</p>
                </div>
              )}
              {editReport.adminNotes && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Previous admin notes:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editReport.adminNotes}</p>
                </div>
              )}
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Internal notes about this bug..."
                    rows={3}
                  />
                </div>
              </div>
              <ReplyWaivedCheckbox
                waived={replyWaiver.waived}
                onChange={replyWaiver.setWaived}
                disabled={saving}
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditReport(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteReport} onOpenChange={(open) => !open && setDeleteReport(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Bug Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteReport?.title}&rdquo;? This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReport(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
